import { createRoute } from '@tanstack/react-router';
import { Button, VerydiaWordmark } from '@verydia/ui';
import { LogOut } from 'lucide-react';
import { rootRoute } from '../root.tsx';
import { useAuth } from '../../lib/auth/index.ts';

/**
 * Signed in, but with nothing to see.
 *
 * Reached when a valid account holds no organization membership and no platform
 * role — a real state, not an error: an invitation may have been revoked, or the
 * account may have been created before anyone assigned it anywhere.
 *
 * The page deliberately does not say which of those it is, or name any
 * organization. To someone who should not be here, every extra detail is
 * reconnaissance.
 */
function AccessDeniedPage() {
  const auth = useAuth();

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--vc-surface-sunken)] px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <VerydiaWordmark tone="surface" />
        </div>

        <div className="rounded-xl border border-[var(--vc-border)] bg-[var(--vc-surface)] p-8 shadow-sm">
          <h1 className="text-lg font-semibold text-[var(--vc-text)]">No access yet</h1>
          <p className="mt-2 text-sm text-[var(--vc-text-subtle)]">
            You are signed in{auth.email ? ` as ${auth.email}` : ''}, but this account does not
            belong to an organization on Verydia Control.
          </p>
          <p className="mt-3 text-sm text-[var(--vc-text-subtle)]">
            An administrator of your organization can invite you. If you were expecting access, ask
            them to confirm the invitation was sent to this address.
          </p>

          <Button
            variant="secondary"
            icon={<LogOut />}
            className="mt-6 w-full justify-center"
            onClick={auth.signOut}
          >
            Sign out
          </Button>
        </div>
      </div>
    </main>
  );
}

export const accessDeniedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/access-denied',
  component: AccessDeniedPage,
});
